/**
 * PLAT-11099 — the overlay's fields, as the block the prompt reads (DS5).
 *
 * `composeMessages` appends whatever a caller sends in `inputs.instructions` as
 * `Additional instructions: <text>` and nothing else — no field names, no
 * structure. So the block below is a convention shared between this file and the
 * `Reel Util | Chapterize a Reel` record, and the record owns its definition
 * (PLAT-11152 S2): the half that reads a format is the half that specifies it.
 *
 * Pure — no React, no store access, no network.
 */

export type ChapterizationOptions = {
  /**
   * How many chapters to aim for. `undefined` is auto, and auto is the default:
   * the count is a target the prompt takes seriously and the client never
   * enforces (DS11).
   */
  chapterCount?: number;
  /**
   * PLAT-11153 — may the run decide the order the reel plays in? Off by
   * default: a creator who deliberately sequenced a reel and then asks for
   * chapters is asking for chapters (S1).
   */
  reorder?: boolean;
  /**
   * Order the reel so the voices alternate — a conversation rather than one
   * person at length. Only meaningful alongside `reorder`, which is the only
   * thing that can act on it: with the order fixed there is nothing to vary. The
   * overlay enforces that pairing; this type documents it.
   */
  varySpeakers?: boolean;
  /**
   * PLAT-11154 — the 1-based positions of the Sections the AI may move, as they
   * were numbered in the composed reel. Absent means all of them, which is the
   * default and is composed as `all` rather than as every number in the reel:
   * ticking everything is the ordinary run, not a special case of a subset (S8).
   */
  movableSectionNumbers?: readonly number[];
  /** The creator's own words, or nothing. */
  guidance?: string;
};

/**
 * Every line is emitted, every time, including the ones this story never varies.
 *
 * The prompt is capable of all three parts of chapterization (E2) and has to be
 * told which to perform; a line left out is a capability the model has to guess
 * about, and it guesses in the expensive direction — it reorders. PLAT-11153 and
 * PLAT-11154 change the *value* of a line, never the shape of the block, which is
 * the whole reason the block is fixed here rather than assembled from whichever
 * options happen to be set.
 *
 * Guidance goes last and labelled (S3 there): the creator's free text can say
 * anything, including things that read like directives, and the structural lines
 * have to be read before it.
 */
export function chapterizationInstructions(
  options: ChapterizationOptions,
): string {
  const guidance = options.guidance?.trim();

  return [
    // PLAT-11153 S2 — the whole wire change for reordering is this line's
    // value. The block was built emitting it precisely so that story did not
    // have to reshape anything.
    `Order: ${
      options.reorder ? 'reorder the sections' : 'keep the current order'
    }`,
    // Second, because it qualifies the line above it: it is a rule for *how* to
    // reorder, and it is emitted as `not a factor` on a run that keeps the
    // order rather than left out, so the block's shape never varies.
    `Speaker variety: ${
      options.reorder && options.varySpeakers
        ? 'maximize alternating speakers'
        : 'not a factor'
    }`,
    `Chapters: ${
      options.chapterCount === undefined
        ? 'auto'
        : `exactly ${options.chapterCount}`
    }`,
    `Sections that may move: ${
      options.movableSectionNumbers?.length
        ? [...options.movableSectionNumbers].sort((a, b) => a - b).join(', ')
        : 'all'
    }`,
    `Guidance: ${guidance || 'none'}`,
  ].join('\n');
}
